import { Component, Inject, Input, NgZone, PLATFORM_ID, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isPlatformBrowser } from '@angular/common';
import { ReportService } from 'src/app/reports/services/report.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/common/services/shared.service';

@Component({
  selector: 'app-line-bar-chart',
  templateUrl: './line-bar-chart.component.html',
  styleUrls: ['./line-bar-chart.component.scss']
})
export class LineBarChartComponent {
  lang!: number;
  private root: am5.Root | undefined;
  @Input() data: any = [];
  @Input() lineLabel: any = '';
  @Input() barLabel: any = '';
  @Input() name: string = 'chartDiv';
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private zone: NgZone,
    private reportService: ReportService,
    private translate: TranslateService,
    private sharedService: SharedService
  ) { }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.root) {
      this.root.dispose();
    }
    this.createChart()
  }
  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          f();
        }, 1000)

      });
    }
  }
  createChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(this.name);
      root.setThemes([am5themes_Animated.new(root)]);

      var chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          layout: root.verticalLayout
        })
      );

      chart.leftAxesContainer.set("layout", root.verticalLayout);

      const rawData = [
        { year: "2022", label: "Q3", rent: 1000, yield: 6 },
        { year: "2022", label: "Q4", rent: 1200, yield: 8 },
        { isSpacer: true },
        { year: "2023", label: "Q1", rent: 900, yield: 4 },
        { year: "2023", label: "Q2", rent: 1000, yield: 6 },
        { year: "2023", label: "Q3", rent: 1100, yield: 8 },
        { year: "2023", label: "Q4", rent: 1200, yield: 10 },
        { isSpacer: true },
        { year: "2024", label: "Q1", rent: 900, yield: 4 },
        { year: "2024", label: "Q2", rent: 950, yield: 6 },
        { year: "2024", label: "Q3", rent: 1000, yield: 8 },
        { year: "2024", label: "Q4", rent: 1200, yield: 12 },
        { isSpacer: true },
        { year: "2025", label: "Q1", rent: 1000, yield: 4 },
        { year: "2025", label: "Q2", rent: 1200, yield: 8 }
      ];

      const labeledData = rawData.map((item, index) => {
        if (item.isSpacer) {
          return {
            category: `spacer-${index}`,
            displayLabel: "",
            group: "",
            isSpacer: true
          };
        }
        return {
          ...item,
          category: `Q${index + 1}`,
          displayLabel: item.label,
          group: item.year
        };
      });

      var xRenderer = am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 20
      });
      xRenderer.grid.template.set("visible", false);

      xRenderer.labels.template.setAll({
        oversizedBehavior: "wrap",
        text: "{displayLabel}",
        centerY: am5.p50,
        centerX: am5.p50,
        fontSize: 12
      });

      xRenderer.labels.template.adapters.add("text", function (text, target) {
        const dataItem:any = target.dataItem;
        if (dataItem && dataItem.dataContext.isSpacer) return "";
        return text;
      });

      var xAxis:any = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "category",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {})
        })
      );
      xAxis.data.setAll(labeledData);
      xAxis.set("groupFields", ["group"]);

      var yearRenderer = am5xy.AxisRendererX.new(root, {
        opposite: false,
        pan: "none",
        strokeOpacity: 0
      });
      yearRenderer.grid.template.set("visible", false);
      yearRenderer.labels.template.setAll({
        fontSize: 12,
        fontWeight: "bold",
        centerX: am5.p50,
        centerY: am5.p100,
        dy: 20
      });

      var yearAxis:any = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "group",
          renderer: yearRenderer,
          groupData: true
        })
      );

      const yearData = [...new Set(rawData.filter(d => !d.isSpacer).map(item => item.year))]
        .map(year => ({ group: year }));
      yearAxis.data.setAll(yearData);

      yearRenderer.labels.template.adapters.add("x", function (x, target:any) {
        if (!target.dataItem) return x;
        const group = target.dataItem.get("category");
        const groupItems = labeledData.filter(item => item.group === group);
        const positions = groupItems.map(item => {
          const dataItem = xAxis.dataItems.find((di:any) => di.get("category") === item.category);
          if (dataItem) {
            const start = xAxis.getDataItemCoordinateX(dataItem, "category", 0);
            const end = xAxis.getDataItemCoordinateX(dataItem, "category", 1);
            return (start + end) / 2;
          }
          return 0;
        }).filter(pos => pos !== 0);
        if (positions.length) {
          const minPos = Math.min(...positions);
          const maxPos = Math.max(...positions);
          return (minPos + maxPos) / 2;
        }
        return x;
      });

      // Yield Y-Axis
      var yieldYAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          max: 15,
          extraMax: 0.2,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0
          })
        })
      );
      yieldYAxis.get("renderer").labels.template.setAll({ visible: false });

      // Add this to hide the bottom grid line
      yieldYAxis.get("renderer").grid.template.adapters.add("visible", function (visible, target:any) {
        const value = target.dataItem.get("value");
        if (value === 0) {
          return false; // Hide the grid line at value 0 (bottom line)
        }
        return visible;
      });

      // Rent Y-Axis
      var rentYAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0
          })
        })
      );
      rentYAxis.get("renderer").labels.template.setAll({ visible: false });

      var rentSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: this.barLabel,
          xAxis: xAxis,
          yAxis: rentYAxis,
          valueYField: "rent",
          categoryXField: "category",
          clustered: false,
          tooltip: am5.Tooltip.new(root, {
            labelText: "{name}: {valueY} KWD/sqm"
          })
        })
      );

      rentSeries.columns.template.setAll({
        fill: am5.color(0x1a73e8),
        stroke: am5.color(0x1a73e8),
        width: am5.percent(50),
        cornerRadiusTL: 4,
        cornerRadiusTR: 4
      });

      rentSeries.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationY: 0,
          sprite: am5.Label.new(root, {
            text: "{valueY}",
            populateText: true,
            fill: am5.color(0xffffff),
            centerY: am5.p50,
            fontSize: 9,
            fontWeight: "bold",
            rotation: 270
          })
        });
      });

      rentSeries.data.setAll(labeledData.filter(item => !item.isSpacer));

      var yieldSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: this.lineLabel,
          xAxis: xAxis,
          yAxis: yieldYAxis,
          valueYField: "yield",
          categoryXField: "category",
          stroke: am5.color(0xff6b00),
          tooltip: am5.Tooltip.new(root, {
            labelText: "{name}: {valueY}%"
          })
        })
      );

      yieldSeries.strokes.template.setAll({
        strokeWidth: 1
      });

      yieldSeries.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 1.5,
            fill: am5.color(0xff6b00),
            stroke: am5.color(0xff6b00),
            strokeWidth: 2
          })
        });
      });

      yieldSeries.bullets.push(function (root, series) {
        let container = am5.Container.new(root, {
          layout: root.verticalLayout,
          centerX: am5.p50,
          centerY: 35
        });

        let label = am5.Label.new(root, {
          text: "{valueY}%",
          fontSize: 9,
          fontWeight: "bold",
          populateText: true,
          background: am5.RoundedRectangle.new(root, {
            fill: am5.color(0xC3D5F8),
            cornerRadiusTL: 4,
            cornerRadiusTR: 4,
            cornerRadiusBL: 4,
            cornerRadiusBR: 4
          }),
          paddingLeft: 5,
          paddingRight: 5,
          paddingTop: 2,
          paddingBottom: 2,
          centerX: am5.p50
        });

        let triangle = am5.Polygon.new(root, {
          points: [
            { x: -5, y: 0 },
            { x: 5, y: 0 },
            { x: 0, y: 6 }
          ],
          fill: am5.color(0xC3D5F8),
          centerX: am5.p50
        });

        container.children.push(label);
        container.children.push(triangle);

        return am5.Bullet.new(root, {
          locationY: 1,
          sprite: container
        });
      });

      yieldSeries.data.setAll(labeledData.filter(item => !item.isSpacer));

      chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50
        })
      ).data.setAll([rentSeries, yieldSeries]);

      chart.appear(1000, 100);
    })
  }
}
